import React, { useState } from "react";
import { Link } from "react-router-dom";
import { businessEventLabel, severityLabels } from "./businessEventLabels";
import type { BusinessEventDto } from "./businessEventTypes";

type Props = {
  event: BusinessEventDto | null;
  onClose: () => void;
};

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function severityClass(severity: BusinessEventDto["severity"]): string {
  if (severity === "CRITICAL") return "border-rose-500/40 bg-rose-500/10 text-rose-200";
  if (severity === "WARNING") return "border-amber-500/40 bg-amber-500/10 text-amber-200";
  return "border-sky-500/30 bg-sky-500/10 text-sky-200";
}

export function BusinessEventDetailDrawer({ event, onClose }: Props): React.JSX.Element | null {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  if (!event) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/70 backdrop-blur-sm">
      <button type="button" aria-label="Fermer" className="flex-1" onClick={onClose} />
      <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Détail événement</p>
            <h2 className="mt-2 text-xl font-semibold text-white">{event.title || businessEventLabel(event.type)}</h2>
            <p className="mt-1 text-sm text-slate-400">{formatDate(event.createdAt)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-700 px-3 py-1 text-sm text-slate-300 hover:bg-slate-800">
            Fermer
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${severityClass(event.severity)}`}>
            {severityLabels[event.severity]}
          </span>
          <span className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-300">
            {businessEventLabel(event.type)}
          </span>
        </div>

        {event.message ? <p className="mt-5 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-200">{event.message}</p> : null}

        <div className="mt-6 grid gap-3 text-sm">
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Acteur</p>
            <p className="mt-1 text-slate-100">{event.actor?.name ?? "Action système"}</p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Entités liées</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {event.client && event.links.client ? <Link className="rounded-lg bg-slate-800 px-3 py-1 text-sky-200 hover:bg-slate-700" to={event.links.client}>Client {event.client.name}</Link> : null}
              {event.passage && event.links.passage ? <Link className="rounded-lg bg-slate-800 px-3 py-1 text-sky-200 hover:bg-slate-700" to={event.links.passage}>Passage {event.passage.reference}</Link> : null}
              {event.tournee && event.links.tournee ? <Link className="rounded-lg bg-slate-800 px-3 py-1 text-sky-200 hover:bg-slate-700" to={event.links.tournee}>Tournée {event.tournee.reference}</Link> : null}
              {event.links.facture ? (
                <Link className="rounded-lg bg-slate-800 px-3 py-1 text-sky-200 hover:bg-slate-700" to={event.links.facture}>
                  Facturation
                </Link>
              ) : null}
              {event.equipe ? <span className="rounded-lg bg-slate-800 px-3 py-1 text-slate-200">Équipe {event.equipe.name}</span> : null}
              {!event.client && !event.passage && !event.tournee && !event.links.facture && !event.equipe ? (
                <span className="text-slate-500">Aucune entité liée.</span>
              ) : null}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowTechnicalDetails((value) => !value)}
          className="mt-6 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800"
        >
          {showTechnicalDetails ? "Masquer détails techniques" : "Afficher détails techniques"}
        </button>
        {showTechnicalDetails ? (
          <pre className="mt-3 max-h-80 overflow-auto rounded-xl border border-slate-800 bg-slate-900 p-4 text-xs text-slate-300">
            {JSON.stringify({ type: event.type, entityType: event.entityType, entityId: event.entityId, payload: event.payload }, null, 2)}
          </pre>
        ) : null}
      </aside>
    </div>
  );
}
